/*
  Custom: Bundle deals — quantity-break tiles.

  Tier 1 behaves like a normal add-to-cart (Dawn's own form handles it).
  Multi-packs let the shopper pick the variant of each unit, so the add is
  intercepted and posted as an `items` array to /cart/add.js.

  Pricing shown here is DISPLAY ONLY. The real reduction is applied by Shopify
  AUTOMATIC discounts (product class, minimum-quantity). The percentages
  configured on the block must match those discounts.
*/
if (!customElements.get('bundle-deals')) {
  customElements.define(
    'bundle-deals',
    class BundleDeals extends HTMLElement {
      constructor() {
        super();
        this.sectionId = this.dataset.sectionId;
        /*
          Some currency formats wrap the amount in markup, e.g.
          `<span class=money>${{amount}}</span>`. We write prices with
          textContent, so strip the tags or they render as literal text.
          (Seen in the theme customizer, where the USD format is used.)
        */
        this.moneyFormat = (this.dataset.moneyFormat || '${{amount}}').replace(/<[^>]*>/g, '');
        this.itemLabel = this.dataset.itemLabel || 'Item';
        this.unavailableText = this.dataset.unavailableText || 'Unavailable';
        // 'percentage' (per-unit %) or 'amount' (flat money off the bundle)
        this.discountType = this.dataset.discountType || 'percentage';
        this.variantId = this.dataset.variantId;

        try {
          this.productData = JSON.parse(this.querySelector('[data-bundle-product]')?.textContent || '{}');
        } catch (e) {
          this.productData = {};
        }

        this.variants = this.productData.variants || [];
        this.optionNames = this.productData.optionNames || [];
        this.tiers = Array.from(this.querySelectorAll('.bundle-deals__input'));
      }

      connectedCallback() {
        this.tiers.forEach((input) => input.addEventListener('change', this.onTierChange.bind(this)));

        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
            if (!event?.data?.variant) return;
            this.variantId = event.data.variant.id.toString();
            this.syncItemsToCurrentVariant();
            this.render();
          });
        }

        // Intercept on the capture phase so we run before Dawn's product-form
        // submit handler, which is bound directly to the form element.
        this.submitHandler = this.onFormSubmit.bind(this);
        document.addEventListener('submit', this.submitHandler, true);

        const checked = this.tiers.find((input) => input.checked);
        if (checked) this.activateTier(checked);
        this.render();
      }

      disconnectedCallback() {
        if (this.variantChangeUnsubscriber) this.variantChangeUnsubscriber();
        document.removeEventListener('submit', this.submitHandler, true);
      }

      /* ---------- tier selection ---------- */

      onTierChange(event) {
        this.activateTier(event.currentTarget);
      }

      activateTier(input) {
        this.setQuantity(parseInt(input.dataset.quantity, 10));

        // Only the active tier shows its per-unit pickers.
        this.querySelectorAll('[data-bundle-items]').forEach((container) => {
          const tierInput = container.closest('.bundle-deals__tier')?.querySelector('.bundle-deals__input');
          if (tierInput === input) {
            this.buildItems(container, parseInt(input.dataset.quantity, 10));
            container.hidden = false;
          } else {
            container.hidden = true;
            container.innerHTML = '';
          }
        });

        this.render();
      }

      get activeTier() {
        return this.tiers.find((input) => input.checked);
      }

      /* ---------- per-unit variant pickers ---------- */

      get currentVariant() {
        return this.variants.find((v) => v.id.toString() === this.variantId?.toString()) || this.variants[0];
      }

      buildItems(container, quantity) {
        if (container.children.length === quantity) return; // already built
        container.innerHTML = '';

        const base = this.currentVariant;
        for (let i = 0; i < quantity; i += 1) {
          container.appendChild(this.buildItemRow(i, base));
        }
        this.updateItemStates(container);
      }

      buildItemRow(index, baseVariant) {
        const row = document.createElement('div');
        row.className = 'bundle-deals__item';
        row.dataset.index = index;

        const label = document.createElement('span');
        label.className = 'bundle-deals__item-label';
        label.textContent = `${this.itemLabel} ${index + 1}`;
        row.appendChild(label);

        const selects = document.createElement('div');
        selects.className = 'bundle-deals__item-options';
        if (this.optionNames.length === 1) selects.classList.add('bundle-deals__item-options--single');

        this.optionNames.forEach((optionName, optionIndex) => {
          const field = document.createElement('label');
          field.className = 'bundle-deals__field';

          const caption = document.createElement('span');
          caption.className = 'visually-hidden';
          caption.textContent = `${this.itemLabel} ${index + 1} — ${optionName}`;
          field.appendChild(caption);

          const select = document.createElement('select');
          select.className = 'bundle-deals__select select__select';
          select.dataset.optionIndex = optionIndex;
          select.addEventListener('change', () => {
            this.updateItemStates(select.closest('[data-bundle-items]'));
            this.render();
          });

          this.optionValues(optionIndex).forEach((value) => {
            const option = document.createElement('option');
            option.value = value;
            option.textContent = value;
            if (baseVariant && baseVariant.options[optionIndex] === value) option.selected = true;
            select.appendChild(option);
          });

          field.appendChild(select);
          selects.appendChild(field);
        });

        row.appendChild(selects);

        const note = document.createElement('span');
        note.className = 'bundle-deals__item-note';
        note.hidden = true;
        row.appendChild(note);

        return row;
      }

      optionValues(optionIndex) {
        const values = [];
        this.variants.forEach((variant) => {
          const value = variant.options[optionIndex];
          if (value && !values.includes(value)) values.push(value);
        });
        return values;
      }

      /* Resolve each row to a variant, and grey out combinations that don't exist. */
      updateItemStates(container) {
        if (!container) return;

        Array.from(container.querySelectorAll('.bundle-deals__item')).forEach((row) => {
          const selects = Array.from(row.querySelectorAll('.bundle-deals__select'));
          const chosen = selects.map((select) => select.value);

          // Disable values that cannot combine with the row's other selections.
          selects.forEach((select, optionIndex) => {
            Array.from(select.options).forEach((option) => {
              const candidate = chosen.slice();
              candidate[optionIndex] = option.value;
              const match = this.variants.find((v) => v.options.every((value, i) => value === candidate[i]));
              option.disabled = !match || !match.available;
            });
          });

          const variant = this.variants.find((v) => v.options.every((value, i) => value === chosen[i]));
          row.dataset.variantId = variant && variant.available ? variant.id : '';

          const note = row.querySelector('.bundle-deals__item-note');
          if (note) {
            const unavailable = !variant || !variant.available;
            note.textContent = unavailable ? this.unavailableText : '';
            note.hidden = !unavailable;
          }
          row.classList.toggle('bundle-deals__item--unavailable', !variant || !variant.available);
        });
      }

      /* When the main variant picker changes, move any rows still on the old
         selection onto the new one, so the widget and the picker agree. */
      syncItemsToCurrentVariant() {
        const container = this.querySelector('[data-bundle-items]:not([hidden])');
        if (!container) return;

        const base = this.currentVariant;
        if (!base) return;

        Array.from(container.querySelectorAll('.bundle-deals__item')).forEach((row, index) => {
          if (index !== 0) return; // only the first unit follows the main picker
          row.querySelectorAll('.bundle-deals__select').forEach((select, optionIndex) => {
            select.value = base.options[optionIndex];
          });
        });
        this.updateItemStates(container);
      }

      /* The variant ids this widget will add, in order. */
      selectedVariantIds() {
        const container = this.querySelector('[data-bundle-items]:not([hidden])');
        const quantity = parseInt(this.activeTier?.dataset.quantity || '1', 10);

        if (!container || !container.children.length) {
          return new Array(quantity).fill(this.variantId);
        }

        return Array.from(container.querySelectorAll('.bundle-deals__item')).map((row) => row.dataset.variantId);
      }

      /* ---------- quantity plumbing (tier 1 / no-variant products) ---------- */

      get quantityInput() {
        const themeInput = document.getElementById(`Quantity-${this.sectionId}`);

        if (themeInput) {
          // Never submit two `quantity` fields: drop our fallback if the
          // theme's input (re)appeared, e.g. after a variant section render.
          if (this._fallbackInput) {
            this._fallbackInput.remove();
            this._fallbackInput = null;
          }
          return themeInput;
        }

        return this.fallbackInput;
      }

      get fallbackInput() {
        if (this._fallbackInput) return this._fallbackInput;

        const formId = this.dataset.productFormId;
        if (!formId) return null;

        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'quantity';
        input.value = '1';
        input.setAttribute('form', formId);
        this.appendChild(input);
        this._fallbackInput = input;
        return input;
      }

      setQuantity(quantity) {
        const input = this.quantityInput;
        if (!input || !quantity) return;

        input.value = quantity;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }

      /* ---------- add to cart ---------- */

      get productForm() {
        return document.getElementById(this.dataset.productFormId);
      }

      onFormSubmit(event) {
        if (event.target !== this.productForm) return;

        const ids = this.selectedVariantIds();
        const quantity = parseInt(this.activeTier?.dataset.quantity || '1', 10);

        // Nothing custom needed for a single unit — let Dawn handle it.
        if (quantity < 2) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        if (ids.some((id) => !id)) {
          this.showFormError(this.unavailableText);
          return;
        }

        this.addItems(ids);
      }

      showFormError(message) {
        const wrapper = document.querySelector(`#${this.dataset.productFormId}`)
          ?.closest('product-form')
          ?.querySelector('.product-form__error-message-wrapper');
        if (!wrapper) return;
        wrapper.querySelector('.product-form__error-message').textContent = message;
        wrapper.removeAttribute('hidden');
      }

      addItems(ids) {
        const productFormElement = this.productForm?.closest('product-form');
        const submitButton = productFormElement?.querySelector('[type="submit"]');
        const spinner = productFormElement?.querySelector('.loading__spinner');
        const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');

        // Collapse duplicates into quantities so the cart shows one line per variant.
        const counts = ids.reduce((acc, id) => acc.set(id, (acc.get(id) || 0) + 1), new Map());
        const items = Array.from(counts, ([id, quantity]) => ({ id: Number(id), quantity }));

        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
          cart.setActiveElement(document.activeElement);
        }

        submitButton?.setAttribute('aria-disabled', true);
        submitButton?.classList.add('loading');
        spinner?.classList.remove('hidden');

        fetch(window.routes.cart_add_url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
          body: JSON.stringify(body),
        })
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              this.showFormError(response.description || response.message);
              return;
            }

            if (!cart) {
              window.location = window.routes.cart_url;
              return;
            }

            publish(PUB_SUB_EVENTS.cartUpdate, {
              source: 'bundle-deals',
              productVariantId: ids[0],
              cartData: response,
            });
            cart.renderContents(response);
          })
          .catch((e) => console.error(e))
          .finally(() => {
            submitButton?.classList.remove('loading');
            submitButton?.removeAttribute('aria-disabled');
            spinner?.classList.add('hidden');
            if (cart?.classList.contains('is-empty')) cart.classList.remove('is-empty');
          });
      }

      /* ---------- pricing ---------- */

      render() {
        const fallbackUnit = this.currentVariant?.price;
        if (fallbackUnit === undefined) return;

        const activeTier = this.activeTier;

        this.tiers.forEach((input) => {
          const tier = input.closest('.bundle-deals__tier');
          if (!tier) return;

          const quantity = parseInt(input.dataset.quantity, 10);
          const percent = parseFloat(input.dataset.discount) || 0;

          // The selected tier prices its actual chosen units; the others show
          // an estimate based on the currently selected variant.
          let unitPrices = new Array(quantity).fill(fallbackUnit);
          if (input === activeTier) {
            const container = tier.querySelector('[data-bundle-items]:not([hidden])');
            if (container && container.children.length) {
              unitPrices = Array.from(container.querySelectorAll('.bundle-deals__item')).map((row) => {
                const variant = this.variants.find((v) => v.id.toString() === row.dataset.variantId);
                return variant ? variant.price : fallbackUnit;
              });
            }
          }

          const full = unitPrices.reduce((sum, price) => sum + price, 0);

          /*
            Match Shopify's own arithmetic exactly, or the widget will promise a
            saving the cart doesn't apply.

            Percentage: Shopify discounts EACH UNIT and truncates to the cent,
            then multiplies — it does not round the line total. Verified against
            a live cart: 2495c at 7% gives floor(174.65) x 2 = 348, not
            round(4990 x 0.07) = 349.

            Amount: a flat sum off the whole bundle, capped at the bundle price
            so a large flat amount can never show a negative total.
          */
          let discount;
          if (this.discountType === 'amount') {
            discount = Math.min(parseInt(input.dataset.amount, 10) || 0, full);
          } else {
            discount = unitPrices.reduce(
              (sum, price) => sum + Math.floor((price * percent) / 100),
              0
            );
          }
          const total = full - discount;

          const totalEl = tier.querySelector('.bundle-deals__total');
          const compareEl = tier.querySelector('.bundle-deals__compare');
          const unitEl = tier.querySelector('.bundle-deals__unit');
          const saveEl = tier.querySelector('.bundle-deals__save');

          if (saveEl) {
            const savings = full - total;
            saveEl.textContent = saveEl.dataset.template.replace('[amount]', this.formatMoney(savings));
            saveEl.hidden = savings <= 0;
          }

          if (totalEl) totalEl.textContent = this.formatMoney(total);
          if (compareEl) {
            compareEl.textContent = percent > 0 ? this.formatMoney(full) : '';
            compareEl.hidden = percent <= 0;
          }
          if (unitEl) {
            unitEl.textContent = unitEl.dataset.template.replace(
              '[price]',
              this.formatMoney(Math.round(total / quantity))
            );
          }
        });
      }

      /* Minimal implementation of Shopify's money_format placeholders. */
      formatMoney(cents) {
        const format = this.moneyFormat;
        const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);
        const type = placeholder ? placeholder[1] : 'amount';

        const withCommas = (value, decimals, thousands, decimal) => {
          const fixed = (value / 100).toFixed(decimals);
          const parts = fixed.split('.');
          parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousands);
          return parts.join(decimal);
        };

        let value;
        switch (type) {
          case 'amount_no_decimals':
            value = withCommas(cents, 0, ',', '.');
            break;
          case 'amount_with_comma_separator':
            value = withCommas(cents, 2, '.', ',');
            break;
          case 'amount_no_decimals_with_comma_separator':
            value = withCommas(cents, 0, '.', ',');
            break;
          default:
            value = withCommas(cents, 2, ',', '.');
        }

        return format.replace(/\{\{\s*\w+\s*\}\}/, value);
      }
    }
  );
}
